import React, { useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Crown, ChevronRight } from "lucide-react";
import { resolveAsset } from "../lib/api";

function initials(name) {
  if (!name) return "?";
  return name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

/** Group Advancement view — one column per round, each match = 4 player rows.
 *  Connector lines link each source match to its rank-1 and rank-2 destinations.
 *  Grey by default, red once the source match is completed.
 */
function PlayerRow({ team, score, rank, isChampion, placementsKnown, advancing }) {
  const advColor = placementsKnown && advancing;
  return (
    <div
      data-testid={isChampion ? `champion-${team?.id}` : undefined}
      className={`flex items-center gap-2 px-3 py-1.5 text-[12px] border-t border-white/5
        ${advColor ? "bg-gradient-to-r from-yellow-400/10 to-transparent text-white" : team ? "text-zinc-300" : "text-zinc-600"}`}
    >
      {isChampion && (
        <Crown
          size={12}
          className="text-yellow-300 drop-shadow-[0_0_6px_rgba(253,224,71,0.9)] shrink-0 champion-crown"
          fill="currentColor"
          strokeWidth={2}
        />
      )}
      {team?.logo_url ? (
        <img
          src={resolveAsset(team.logo_url)}
          alt=""
          className="h-5 w-5 rounded-full object-cover"
          style={{ background: team.color || "#E60000" }}
        />
      ) : (
        <div
          className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white/90"
          style={{ background: team?.color || "#2a2a2a" }}
        >
          {team ? initials(team.name) : "—"}
        </div>
      )}
      <span className="flex-1 truncate font-medium">
        {team?.name || <span className="text-zinc-600">TBD</span>}
      </span>
      {rank ? (
        <span
          className={`text-[9px] font-mono tracking-widest px-1 py-0.5 rounded
            ${advColor ? "text-yellow-200 bg-yellow-400/20" : "text-zinc-500 bg-white/5"}`}
        >
          #{rank}
        </span>
      ) : null}
      <span className="font-mono font-semibold w-8 text-right text-[13px]">
        {team ? score : "–"}
      </span>
    </div>
  );
}

const GroupCard = React.forwardRef(function GroupCard(
  { match, teamsById, onClick, justUpdated, championTeamId },
  ref,
) {
  const pids = match.participant_ids || [];
  const scores = match.participant_scores || [0, 0, 0, 0];
  const placements = match.final_placements || [];
  const placementsKnown = match.status === "completed" && placements.length >= 2;
  const rankById = {};
  if (placementsKnown) {
    placements.forEach((id, i) => { if (id) rankById[id] = i + 1; });
  }
  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      onClick={() => onClick && onClick(match)}
      data-testid={`match-${match.id}`}
      className={`match-card group-card cursor-pointer overflow-hidden ${justUpdated ? "just-updated" : ""}
        ${match.status === "live" ? "live" : ""}`}
      style={{ minWidth: 240 }}
    >
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-white/5 flex justify-between items-center">
        <span>{match.round_name || `Round ${match.round + 1}`}</span>
        <span className="text-zinc-600">
          Match {(match.position ?? 0) + 1}
        </span>
      </div>
      {[0, 1, 2, 3].map((slot) => {
        const pid = pids[slot];
        const team = pid ? teamsById[pid] : null;
        const rank = pid ? rankById[pid] : null;
        const advancing = rank === 1 || rank === 2;
        const isChampion = championTeamId && pid === championTeamId;
        return (
          <PlayerRow
            key={slot}
            team={team}
            score={scores[slot] ?? 0}
            rank={rank}
            placementsKnown={placementsKnown}
            advancing={advancing}
            isChampion={isChampion}
          />
        );
      })}
    </motion.div>
  );
});

const GREY = "#555555"; // matches Bracket.jsx
const RED = "#e60000";

export default function GroupAdvancementView({
  matches, teamsById, onMatchClick, recentlyUpdated, championTeamId,
}) {
  // Group matches by round
  const byRound = {};
  matches.forEach((m) => {
    if (!byRound[m.round]) byRound[m.round] = [];
    byRound[m.round].push(m);
  });
  const rounds = Object.keys(byRound)
    .map((r) => parseInt(r, 10))
    .sort((a, b) => a - b);

  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const matchRefs = useRef({});
  const [lines, setLines] = useState([]);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const compute = () => {
      const root = innerRef.current;
      if (!root) return;
      const rootRect = root.getBoundingClientRect();
      setSize({
        w: root.scrollWidth,
        h: root.scrollHeight,
      });
      const nextLines = [];

      // Group source matches by their target (next match). One source can
      // feed multiple unique targets (e.g. 1st-place → winners bracket,
      // 2nd-place → seconds bracket) — handled by bucketing per target.
      const groups = {}; // targetId -> [source match,...]
      matches.forEach((m) => {
        const nexts = (m.next_match_ids_by_rank || []).filter(Boolean);
        const unique = [...new Set(nexts)];
        unique.forEach((nid) => {
          if (!matchRefs.current[m.id] || !matchRefs.current[nid]) return;
          if (!groups[nid]) groups[nid] = [];
          groups[nid].push(m);
        });
      });

      Object.entries(groups).forEach(([targetId, sources]) => {
        const targetEl = matchRefs.current[targetId];
        if (!targetEl) return;
        const tb = targetEl.getBoundingClientRect();
        const tx = tb.left - rootRect.left;
        const ty = tb.top + tb.height / 2 - rootRect.top;

        const infos = sources
          .map((m) => {
            const el = matchRefs.current[m.id];
            const fb = el.getBoundingClientRect();
            return {
              match: m,
              x: fb.right - rootRect.left,
              y: fb.top + fb.height / 2 - rootRect.top,
            };
          })
          .sort((a, b) => a.y - b.y);

        const sourceX = Math.max(...infos.map((s) => s.x));
        const midX = (sourceX + tx) / 2;

        if (infos.length === 1) {
          const s = infos[0];
          const completed = s.match.status === "completed";
          nextLines.push({
            id: `solo-${s.match.id}-${targetId}`,
            d: `M ${s.x} ${s.y} H ${midX} V ${ty} H ${tx}`,
            completed,
          });
          return;
        }

        // Two (or more) sources merging — classic bracket shape.
        //   - Default: every segment GREY.
        //   - Each source's stub + spine segment turns RED once that source
        //     match is completed.
        //   - The handoff into the target turns RED once ANY feeder completed.
        const top = infos[0];
        const bot = infos[infos.length - 1];

        infos.forEach((s, idx) => {
          const completed = s.match.status === "completed";
          nextLines.push({
            id: `stub-${s.match.id}-${targetId}`,
            d: `M ${s.x} ${s.y} H ${midX}`,
            completed,
          });
          const spineY = idx === 0 ? top.y : bot.y;
          nextLines.push({
            id: `spine-${targetId}-${idx}-${s.match.id}`,
            d: `M ${midX} ${spineY} V ${ty}`,
            completed,
          });
        });

        const anyCompleted = infos.some((s) => s.match.status === "completed");
        nextLines.push({
          id: `hand-${targetId}`,
          d: `M ${midX} ${ty} H ${tx}`,
          completed: anyCompleted,
        });
      });

      setLines(nextLines);
    };

    compute();
    // Re-measure on content changes / resizes
    const obs = new ResizeObserver(() => compute());
    if (innerRef.current) obs.observe(innerRef.current);
    Object.values(matchRefs.current).forEach((el) => el && obs.observe(el));
    window.addEventListener("resize", compute);
    // Extra compute passes to catch framer-motion layout settle
    const r1 = requestAnimationFrame(compute);
    const t1 = setTimeout(compute, 120);
    const t2 = setTimeout(compute, 360);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", compute);
      cancelAnimationFrame(r1);
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  return (
    <div ref={containerRef} className="bracket-scroll">
      <div ref={innerRef} className="relative flex items-start p-6 gap-20 min-w-max">
        <svg
          className="absolute inset-0 pointer-events-none"
          width={size.w}
          height={size.h}
          style={{ zIndex: 0 }}
        >
          {lines.map((l) => (
            <path
              key={l.id}
              d={l.d}
              fill="none"
              stroke={l.completed ? RED : GREY}
              strokeWidth={3}
              strokeLinejoin="miter"
              strokeLinecap="square"
              shapeRendering="crispEdges"
              style={{
                transition: "stroke 320ms ease",
                filter: l.completed ? "drop-shadow(0 0 4px rgba(230,0,0,0.6))" : undefined,
              }}
            />
          ))}
        </svg>

        <div className="relative flex items-start gap-24" style={{ zIndex: 1 }}>
          {rounds.map((r, idx) => {
            const col = byRound[r].sort((a, b) => a.position - b.position);
            const isFinalRound = idx === rounds.length - 1;
            const completedCount = col.filter((m) => m.status === "completed").length;
            const totalCount = col.length;
            const allDone = totalCount > 0 && completedCount === totalCount;
            // Bracket-style vertical spacing: round r's matches are spaced
            // 2^idx apart so later-round match centers line up between
            // their two source-round matches. Also pushes the first match
            // down so the column as a whole stays centered relative to
            // the feeder column — this is what makes Round 3 → Final
            // connectors hit the Final card in the middle instead of off-top.
            const BASE_CARD_STACK = 24; // px — gap between R1 matches (same as gap-6)
            const CARD_H_EST = 260;     // px — 4-row group card rough height; visuals use flex layout so this is only the connector-alignment target
            const stride = (CARD_H_EST + BASE_CARD_STACK) * (2 ** idx);
            const topOffset = idx === 0 ? 0 : (stride - (CARD_H_EST + BASE_CARD_STACK)) / 2;
            return (
              <div
                key={r}
                className="flex flex-col min-w-[260px]"
                style={{ paddingTop: topOffset }}
              >
                <div className="mx-auto w-full max-w-[240px] pb-1 text-center text-[11px] uppercase tracking-[0.2em] text-red-400/90 font-mono font-semibold flex items-center justify-center gap-1.5 flex-wrap">
                  <span className="truncate">{col[0]?.round_name || `Round ${r + 1}`}</span>
                  {totalCount > 1 && (
                    <span
                      data-testid={`round-progress-${r}`}
                      className={`inline-flex items-center text-[9px] tracking-widest rounded-full px-1.5 py-0.5 border whitespace-nowrap
                        ${allDone
                          ? "text-red-200 bg-red-500/15 border-red-500/40 round-complete-pulse"
                          : "text-zinc-400 bg-white/5 border-white/10"}`}
                    >
                      {completedCount}/{totalCount} done
                    </span>
                  )}
                  {!isFinalRound && (
                    <span
                      data-testid={`round-advance-badge-${r}`}
                      className="inline-flex items-center gap-0.5 text-[9px] tracking-widest text-yellow-300/90 bg-yellow-400/10 border border-yellow-400/30 rounded-full px-1.5 py-0.5 whitespace-nowrap"
                    >
                      <ChevronRight size={9} /> Top 2
                    </span>
                  )}
                </div>
                <div
                  className="flex flex-col"
                  style={{ gap: idx === 0 ? BASE_CARD_STACK : (stride - CARD_H_EST) }}
                >
                  {col.map((m) => (
                    <GroupCard
                      key={m.id}
                      ref={(el) => {
                        if (el) matchRefs.current[m.id] = el;
                        else delete matchRefs.current[m.id];
                      }}
                      match={m}
                      teamsById={teamsById}
                      onClick={onMatchClick}
                      justUpdated={Boolean(recentlyUpdated && recentlyUpdated[m.id])}
                      championTeamId={championTeamId}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
